/**
 * Onde está o servidor do RPG Desk (API, WebSocket e arquivos).
 *
 * Normalmente é o mesmo endereço da página: o servidor serve a interface, e no desenvolvimento o
 * Vite repassa `/api` e `/ws` para ele. Com a interface hospedada à parte (Netlify, por exemplo), o
 * build recebe `VITE_SERVER_URL` com o endereço do servidor (`https://api.exemplo.com`), e os
 * caminhos do servidor são resolvidos nele: as chamadas da API e também os endereços guardados nas
 * mesas e mensagens (`/api/files/…`: imagens, sons, anexos, fichas, plugins).
 */

/** Endereço do servidor sem a barra do fim; vazio = o mesmo da página. */
export const SERVER_BASE = normalizeBase(import.meta.env.VITE_SERVER_URL);

export function normalizeBase(value: string | undefined): string {
  return (value ?? '').trim().replace(/\/+$/, '');
}

/**
 * Endereço de um caminho do servidor (`/api/…`) para usar na página: com o servidor à parte, ganha o
 * endereço dele na frente. Endereços completos (`https:`, `data:`, `blob:`) e os de protocolo
 * relativo (`//…`) passam como estão.
 */
export function resolveServerUrl(url: string, base: string): string {
  if (!base || !url.startsWith('/') || url.startsWith('//')) return url;
  return base + url;
}

export function serverUrl(url: string): string {
  return resolveServerUrl(url, SERVER_BASE);
}

/** Endereço do WebSocket do servidor, com a sessão. */
export function socketUrl(token: string, base = SERVER_BASE, page: { protocol: string; host: string } = window.location): string {
  const server = base ? new URL(base) : null;
  const scheme = (server ? server.protocol : page.protocol) === 'https:' ? 'wss' : 'ws';
  const host = server ? server.host : page.host;
  const prefix = server ? server.pathname.replace(/\/+$/, '') : '';
  return `${scheme}://${host}${prefix}/ws?token=${encodeURIComponent(token)}`;
}

/** O endereço é do servidor do RPG Desk (a origem dele, ou a da página quando é o mesmo)? */
export function isServerOrigin(url: URL, base = SERVER_BASE): boolean {
  return url.origin === (base ? new URL(base).origin : window.location.origin);
}
